import React, {useState, useEffect, useMemo, useRef} from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    LineController,
    Title,
    Tooltip,
    Legend,
    TimeScale,
    ChartOptions,
    ChartData,
    TooltipItem,
    Plugin
} from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { Chart } from 'react-chartjs-2';
import 'chartjs-adapter-moment';
import moment from 'moment';
import { getTimeSeriesData } from '../../services/api.service';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, LineController, Title, Tooltip, Legend, TimeScale, zoomPlugin);

interface TimeSeriesDataPoint {
    timestamp: string;
    value: number | null;
}

interface TimeSeriesData {
    data: TimeSeriesDataPoint[];
    metadata: {
        station_id: string;
        variable: string;
        start_date: string;
        end_date: string;
        count: number;
        total_count: number;
    };
}

interface TiltChartProps {
    stationId: string;
    startDate: string;
    endDate: string;
    height?: number;
}

const TiltChart: React.FC<TiltChartProps> = ({
    stationId,
    startDate,
    endDate,
    height = 400
}) => {
    // State management
    const [tiltXData, setTiltXData] = useState<TimeSeriesData | null>(null);
    const [tiltYData, setTiltYData] = useState<TimeSeriesData | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
     const [chartKey, setChartKey] = useState<number>(0); // Add key to force chart re-render

    const chartRef = useRef<any>(null);

     // Reset zoom function that properly accesses the chart instance
    const resetZoom = () => {
        if (chartRef.current) {
            // Access the chart instance through the react-chartjs-2 wrapper
            const chartInstance = chartRef.current;
            if (chartInstance && chartInstance.resetZoom) {
                chartInstance.resetZoom();
            } else if (chartInstance && chartInstance.chart && chartInstance.chart.resetZoom) {
                chartInstance.chart.resetZoom();
            }
        }
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                // Fetch both tilt datasets in parallel
                const [tiltXResponse, tiltYResponse] = await Promise.allSettled([
                    getTimeSeriesData(stationId, 'tx', startDate, endDate), // Tilt X
                    getTimeSeriesData(stationId, 'ty', startDate, endDate)  // Tilt Y
                ]);

                // Handle tilt X data response
                if (tiltXResponse.status === 'fulfilled') {
                    setTiltXData(tiltXResponse.value);
                } else {
                    console.error('Failed to fetch tilt X data:', tiltXResponse.reason);
                }

                // Handle tilt Y data response
                if (tiltYResponse.status === 'fulfilled') {
                    setTiltYData(tiltYResponse.value);
                } else {
                    console.error('Failed to fetch tilt Y data:', tiltYResponse.reason);
                }

            } catch (err) {
                setError('Failed to fetch tilt data');
                console.error('Error fetching tilt data:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [stationId, startDate, endDate]);

    // Chart data configuration
    const chartData: ChartData<'line'> = useMemo(() => {
        const datasets: any[] = [];

        // Tilt X dataset
        if (tiltXData && tiltXData.data && tiltXData.data.length > 0) {
            datasets.push({
                label: 'Tilt X (degrees)',
                data: tiltXData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#dc3545',
                backgroundColor: 'rgba(220, 53, 69, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: false,
                tension: 0.1,
                yAxisID: 'y'
            });
        }

        // Tilt Y dataset
        if (tiltYData && tiltYData.data && tiltYData.data.length > 0) {
            datasets.push({
                label: 'Tilt Y (degrees)',
                data: tiltYData.data
                    .filter(point => point.value !== null)
                    .map(point => ({
                        x: moment(point.timestamp).valueOf(),
                        y: point.value
                    })),
                borderColor: '#fd7e14',
                backgroundColor: 'rgba(253, 126, 20, 0.1)',
                borderWidth: 2,
                pointRadius: 2,
                pointHoverRadius: 4,
                fill: false,
                tension: 0.1,
                yAxisID: 'y'
            });
        }

        return { datasets };
    }, [tiltXData, tiltYData]);

    // Chart options configuration
    const chartOptions: ChartOptions<'line'> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        },
        plugins: {
            title: {
                display: true,
                text: 'Station Tilt Measurements',
                font: {
                    size: 16
                }
            },
            legend: {
                display: true,
                position: 'top'
            },
            tooltip: {
                callbacks: {
                    title: (context) => {
                        if (context.length > 0) {
                            return moment(context[0].parsed.x).format('DD MMM YYYY HH:mm');
                        }
                        return '';
                    },
                    label: (context) => {
                        const value = context.parsed.y;
                        const label = context.dataset.label;
                        return `${label}: ${value?.toFixed(3) || 0}°`;
                    }
                }
            },
             // Zoom plugin configuration
            zoom: {
                zoom: {
                    wheel: {
                        enabled: true,
                    },
                    pinch: {
                        enabled: true
                    },
                    mode: 'x',
                },
                pan: {
                    enabled: true,
                    mode: 'x',
                }
            }
        },
        scales: {
            x: {
                type: 'time',
                time: {
                    displayFormats: {
                        hour: 'HH:mm',
                        day: 'DD/MM',
                        week: 'DD/MM',
                        month: 'MM/YY'
                    },
                    tooltipFormat: 'DD MMM YYYY HH:mm'
                },
                title: {
                    display: true,
                    text: 'Time'
                }
            },
            y: {
                type: 'linear',
                display: true,
                position: 'left',
                title: {
                    display: true,
                    text: 'Tilt (degrees)'
                },
                ticks: {
                    callback: function (value: any) {
                        return `${value}°`;
                    }
                },
                grid: {
                    drawOnChartArea: true,
                }
            }
        },
        parsing: false,
        normalized: true,
        spanGaps: false
    }), []);

    // Loading state
    if (loading) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="d-flex justify-content-center align-items-center" style={{ height: `${height}px` }}>
                        <div className="text-muted">
                            <i className="fa fa-spinner fa-spin fa-3x mb-3"></i>
                            <p>Loading tilt data...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Error state
    if (error) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-danger">{error}</div>
                </div>
            </div>
        );
    }

    // No data state
    if (!tiltXData?.data?.length && !tiltYData?.data?.length) {
        return (
            <div className="card">
                <div className="card-body">
                    <div className="alert alert-warning">No tilt data available</div>
                </div>
            </div>
        );
    }

    // Calculate summary statistics
    const validTiltXData = tiltXData?.data?.filter(point => point.value !== null) || [];
    const validTiltYData = tiltYData?.data?.filter(point => point.value !== null) || [];

    const avgTiltX = validTiltXData.length > 0 ?
        validTiltXData.reduce((sum, d) => sum + d.value!, 0) / validTiltXData.length : 0;

    const avgTiltY = validTiltYData.length > 0 ?
        validTiltYData.reduce((sum, d) => sum + d.value!, 0) / validTiltYData.length : 0;

    const maxTiltX = validTiltXData.length > 0 ?
        Math.max(...validTiltXData.map(d => Math.abs(d.value!))) : 0;

    const maxTiltY = validTiltYData.length > 0 ?
        Math.max(...validTiltYData.map(d => Math.abs(d.value!))) : 0;

    // Calculate overall tilt magnitude (combined X and Y)
    const avgTiltMagnitude = Math.sqrt(avgTiltX * avgTiltX + avgTiltY * avgTiltY);
    const maxTiltMagnitude = Math.sqrt(maxTiltX * maxTiltX + maxTiltY * maxTiltY);

    // Main render
    return (
        <div className="card">
              <div className="card-header">
                <div className="d-flex justify-content-between align-items-center">
                    {/*<h5 className="card-title mb-0">{title}</h5>*/}
                    <div className="d-flex align-items-center gap-3">
                         {/* Reset zoom button */}
                        <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={resetZoom}
                            title="Reset Zoom"
                        >
                            <i className="fa fa-search-minus me-1"></i>
                            Reset Zoom
                        </button>

                    </div>
                </div>
            </div>
            <div className="card-body">
                <div style={{ height: `${height}px` }}>
                    <Chart key={chartKey} ref={chartRef} type="line" data={chartData} options={chartOptions} />
                </div>
                <div className="mt-2">
                    <small className="text-muted">
                        Station tilt measurements over time
                        {validTiltXData.length > 0 && (
                            <span className="ms-3">
                                Tilt X: {validTiltXData.length} points
                            </span>
                        )}
                        {validTiltYData.length > 0 && (
                            <span className="ms-3">
                                Tilt Y: {validTiltYData.length} points
                            </span>
                        )}
                        <span className="ms-3">
                            Avg Tilt X: {avgTiltX.toFixed(3)}°
                        </span>
                        <span className="ms-3">
                            Avg Tilt Y: {avgTiltY.toFixed(3)}°
                        </span>
                        <span className="ms-3">
                            Avg Magnitude: {avgTiltMagnitude.toFixed(3)}°
                        </span>
                        <span className="ms-3">
                            Max Magnitude: {maxTiltMagnitude.toFixed(3)}°
                        </span>
                    </small>
                </div>
            </div>
        </div>
    );
};

export default TiltChart;